/**
 * ManageNotificationsDialog.js
 *
 * A multi-page dialog for managing notification preferences per audience
 * group (Patient, Informant, Provider Alerts).  The dialog has two layers:
 *
 *   1. **Menu view** - lists the three audience groups as compact action rows
 *      (aligned with ManageOrgConfigDialog / ManageInstrumentsDialog style).
 *   2. **Detail view** - shows the toggles for a specific group with a
 *      read-only / editing mode flow:
 *        - Initially read-only (toggles disabled).
 *        - "Make Changes" button enters editing mode.
 *        - "Save Changes" button persists edits and exits editing mode.
 *        - "Cancel" discards edits and returns to read-only.
 *
 * The dialog receives the full notificationConfig as a prop, manages a
 * local working copy for edits, and calls onSave(updatedConfig) when the
 * user commits changes.  This avoids accidental backend writes from
 * stray toggle clicks.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { NOTIFICATION_GROUPS, getNotificationGroupsForModules } from '../config/notificationSettingsConfig';
import InfoTooltip from '../../../../components/InfoTooltip';
import ConfigHistoryTimeline from './ConfigHistoryTimeline';

// ─── Icons (inline SVGs kept small - w-4 h-4 for menu rows) ────────────

const ChevronRightIcon = ({ className = 'w-4 h-4 text-gray-400' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ─── Audience group icons (compact - w-4 h-4 to match org config dialog) ──

const groupIcons = {
  patient: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  informant: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  // CYP parent/carer: single-person icon (one responsible parent/carer)
  cypGuardian: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  // CYP school representative: academic-cap icon
  cypSchoolRep: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  ),
  providerAlerts: (
    <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
};

// Short action labels for the menu rows (aligned with "Manage Patient Notifications" etc.)
const groupActionLabels = {
  patient: 'Edit Patient Emails',
  informant: 'Edit Informant Emails',
  cypGuardian: 'Edit CYP Parent/Carer Emails',
  cypSchoolRep: 'Edit CYP School Representative Emails',
  providerAlerts: 'Edit Provider-specific Alerts',
};

// Sections passed to the history CF for this dialog's audit trail
const HISTORY_SECTIONS = ['notificationConfig'];


export default function ManageNotificationsDialog({
  open,
  onOpenChange,
  notifConfig,
  isSaving,
  onSave,
  supportEmail = '',
  availableModules = ['adultAdhd'],
  organisationName = '',
}) {
  // Groups visible to this organisation: CYP groups only appear when the
  // cypAdhd module is enabled (adult-only orgs never see them).
  const visibleGroups = getNotificationGroupsForModules(availableModules);

  // ── Org-gated item filter ──────────────────────────────────────────
  // Two independent gates on individual notification items:
  //
  // 1. `orgs` — restricts visibility to specific organisation names
  //    (e.g. self_harm_patient_support is SLaM / 3 Borough only).
  //    Items without `orgs` are shown to all orgs.
  //
  // 2. `modules` — restricts visibility to organisations that have at
  //    least one of the listed assessment modules enabled. This is the
  //    same concept as the group-level `modules` on NOTIFICATION_GROUPS
  //    but applied one level deeper, so CYP-specific items within the
  //    shared providerAlerts group are hidden for adult-only orgs.
  //    Items without `modules` are always shown.
  const filterItemsForOrg = useCallback((items) => {
    return items.filter((item) => {
      // Gate 1: org-name restriction
      if (item.orgs) {
        const trimmed = (organisationName || '').trim();
        if (!trimmed || !item.orgs.includes(trimmed)) return false;
      }
      // Gate 2: module restriction
      if (item.modules) {
        if (!availableModules || !item.modules.some((m) => availableModules.includes(m))) {
          return false;
        }
      }
      return true;
    });
  }, [organisationName, availableModules]);
  // ── Navigation state ───────────────────────────────────────────────
  // null = menu view, string = detail view for that group key
  const [activeGroup, setActiveGroup] = useState(null);

  // ── Editing state ──────────────────────────────────────────────────
  // The local working copy is only created when the user clicks
  // "Make Changes".  While null, toggles are read-only.
  const [isEditing, setIsEditing] = useState(false);
  const [workingConfig, setWorkingConfig] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // ── Animated height transition (matches ManageOrgConfigDialog) ─────
  const contentRef = useRef(null);
  const [animatedHeight, setAnimatedHeight] = useState('auto');
  const currentView = activeGroup ? (isEditing ? 'edit' : 'detail') : 'menu';

  const measureAndAnimate = useCallback(() => {
    if (!contentRef.current) return;
    setAnimatedHeight(contentRef.current.scrollHeight + 'px');
  }, []);

  // Reset internal state when the dialog opens/closes
  useEffect(() => {
    if (!open) {
      setActiveGroup(null);
      setIsEditing(false);
      setWorkingConfig(null);
      setSaveSuccess('');
      setSaveError('');
      setAnimatedHeight('auto');
    }
  }, [open]);

  // Re-measure on view changes and when the dialog opens (open is
  // included so the first render gets a pixel height - CSS transitions
  // can't animate from 'auto' to a pixel value).
  useEffect(() => {
    const timer = setTimeout(measureAndAnimate, 30);
    return () => clearTimeout(timer);
  }, [open, currentView, activeGroup, saveSuccess, saveError, measureAndAnimate]);

  // ── Helpers ────────────────────────────────────────────────────────

  const displayConfig = isEditing && workingConfig ? workingConfig : notifConfig;

  const countEnabled = useCallback((groupKey) => {
    const group = notifConfig?.[groupKey] || {};
    // Count stored config entries that are enabled
    let count = Object.values(group).filter((entry) => entry.send !== false).length;
    // Also count org-gated display-only items (e.g. self_harm_patient_support)
    // that are not in the stored config but should show as "always on" for
    // matching orgs. These are items with an `orgs` array and allowDisable: false.
    const meta = visibleGroups.find((g) => g.groupKey === groupKey);
    if (meta) {
      const orgItems = filterItemsForOrg(meta.items);
      for (const item of orgItems) {
        if (item.orgs && !item.allowDisable && !(item.key in group)) {
          count += 1;
        }
      }
    }
    return count;
  }, [notifConfig, visibleGroups, filterItemsForOrg]);

  // Resolve the subtitle for a group, enriching providerAlerts with the
  // support email from the org config when available.
  const getSubtitle = useCallback((groupKey, fallback) => {
    if (groupKey === 'providerAlerts' && supportEmail) {
      return `Notifications sent to your support team inbox at ${supportEmail}`;
    }
    return fallback;
  }, [supportEmail]);

  // True when the working copy differs from the original config,
  // used to prevent unnecessary CF calls when nothing has changed.
  const hasChanges = isEditing && workingConfig
    ? JSON.stringify(workingConfig) !== JSON.stringify(notifConfig)
    : false;

  const enterEditMode = () => {
    setWorkingConfig(JSON.parse(JSON.stringify(notifConfig)));
    setIsEditing(true);
    setSaveSuccess('');
    setSaveError('');
  };

  const cancelEdit = () => {
    setWorkingConfig(null);
    setIsEditing(false);
  };

  const handleToggle = (groupKey, notifKey, field) => {
    if (!isEditing || !workingConfig) return;

    setWorkingConfig((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated[groupKey]) updated[groupKey] = {};
      if (!updated[groupKey][notifKey]) updated[groupKey][notifKey] = {};

      const newValue = !updated[groupKey][notifKey][field];
      updated[groupKey][notifKey][field] = newValue;

      // When Send is turned off, automatically turn off CC Provider too
      if (field === 'send' && !newValue && updated[groupKey][notifKey].ccProvider) {
        updated[groupKey][notifKey].ccProvider = false;
      }

      return updated;
    });
  };

  const handleSave = async () => {
    setSaveError('');
    setSaveSuccess('');
    try {
      await onSave(workingConfig);
      setSaveSuccess('Notification settings saved successfully.');
      setIsEditing(false);
      setWorkingConfig(null);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save notification settings.');
    }
  };

  const navigateBack = () => {
    // Always return to the overview, discarding any unsaved edits
    if (isEditing) cancelEdit();
    setActiveGroup(null);
    setSaveSuccess('');
    setSaveError('');
  };

  // ── Group detail view (the toggle table for a single group) ────────

  const groupMeta = NOTIFICATION_GROUPS.find((g) => g.groupKey === activeGroup);

  // ── Render: Menu view ─────────────────────────────────────────────

  const renderMenu = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-gray-900 pr-6">
        Notification Preferences
      </h3>
      <p className="text-sm text-gray-500">
        Manage which automated notifications are sent by the system for your organisation.
      </p>

      <div className="bg-white border border-gray-200 rounded-md p-3">
        <h4 className="text-xs font-medium text-gray-900 mb-2">Actions</h4>
        <div className="space-y-2">
          {visibleGroups.map(({ groupKey, subtitle: defaultSubtitle }) => {
            const enabledCount = countEnabled(groupKey);
            return (
              <button
                key={groupKey}
                type="button"
                onClick={() => {
                  setActiveGroup(groupKey);
                  setSaveSuccess('');
                  setSaveError('');
                }}
                className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-primary/5 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  {groupIcons[groupKey]}
                  <div>
                    <p className="text-xs font-semibold text-gray-900">
                      {groupActionLabels[groupKey]}
                    </p>
                    <p className="text-xs text-gray-500">
                      {getSubtitle(groupKey, defaultSubtitle)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                    {enabledCount} enabled
                  </span>
                  <ChevronRightIcon />
                </div>
              </button>
            );
          })}

          {/* View History action - shows the audit trail timeline */}
          <button
            type="button"
            onClick={() => {
              setActiveGroup('history');
              setSaveSuccess('');
              setSaveError('');
            }}
            className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-primary/5 hover:border-primary/30 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-xs font-semibold text-gray-900">View History</p>
                <p className="text-xs text-gray-500">See recent notification preference changes</p>
              </div>
            </div>
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    </div>
  );

  // ── Render: Detail view ───────────────────────────────────────────

  const renderDetail = () => {
    if (!groupMeta) return null;

    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={navigateBack}
          className="text-xs text-primary hover:text-primary/80 font-semibold"
        >
          &larr; Back to Overview
        </button>

        <h3 className="text-base font-semibold text-gray-900">
          {isEditing ? `Editing: ${groupActionLabels[activeGroup]}` : groupActionLabels[activeGroup]}
        </h3>
        <p className="text-xs text-gray-500">{getSubtitle(activeGroup, groupMeta.subtitle)}</p>

        {/* Feedback banners */}
        {saveSuccess && (
          <div className="p-3 bg-green-100 text-green-700 rounded-lg text-sm">
            {saveSuccess}
          </div>
        )}
        {saveError && (
          <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {saveError}
          </div>
        )}

        {/* Table wrapper - keeps header and rows as siblings inside a
            single element so the parent's space-y-3 doesn't inject a gap. */}
        <div>
          {/* Column headers - provider alerts use wider 9/3 layout;
              patient/informant groups use 6/3/3 for Send + CC Provider. */}
          {activeGroup === 'providerAlerts' ? (
            <div className="grid grid-cols-12 px-3 py-2 bg-gray-100 rounded-t-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-9">Notification</div>
              <div className="col-span-3 text-center">Enabled</div>
            </div>
          ) : (
            <div className="grid grid-cols-12 px-3 py-2 bg-gray-100 rounded-t-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-6">Notification</div>
              <div className="col-span-3 text-center">Enabled</div>
              <div className="col-span-3 text-center">CC Provider</div>
            </div>
          )}

          {/* Notification rows — filtered by org so org-gated items
              (e.g. self_harm_patient_support for SLaM) only show to
              matching organisations. */}
          <div className="border border-gray-200 border-t-0 rounded-b-lg divide-y divide-gray-100">
          {filterItemsForOrg(groupMeta.items).map((item) => {
            const entry = displayConfig?.[activeGroup]?.[item.key] || {};
            const isSendOn = entry.send !== false;
            const isCcOn = entry.ccProvider !== false;
            const isLocked = !item.allowDisable;
            const isDisabled = !isEditing || isSaving;

            // Provider alerts use wider label column since there's no CC column
            const labelSpan = activeGroup === 'providerAlerts' ? 'col-span-9' : 'col-span-6';

            return (
              <div
                key={item.key}
                className="grid grid-cols-12 items-center px-3 py-3"
              >
                {/* Label + right-aligned tooltip */}
                <div className={`${labelSpan} flex items-center justify-between`}>
                  <span className="text-xs text-gray-800">{item.label}</span>
                  <InfoTooltip
                    content={item.description}
                    side="right"
                    maxWidth="280px"
                  />
                </div>

                {/* Send toggle + lock icon (for non-toggleable rows).
                    The lock is absolutely positioned relative to the toggle
                    button so it hugs the toggle's right edge without shifting
                    the centered alignment. */}
                <div className="col-span-3 flex justify-center">
                  <div className="relative">
                    <button
                      type="button"
                      disabled={isLocked || isDisabled}
                      onClick={() => handleToggle(activeGroup, item.key, 'send')}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1
                        ${isLocked
                          ? 'bg-primary/60 cursor-not-allowed'
                          : isDisabled
                            ? (isSendOn ? 'bg-primary cursor-not-allowed' : 'bg-gray-300 cursor-not-allowed')
                            : isSendOn
                              ? 'bg-primary cursor-pointer'
                              : 'bg-gray-300 cursor-pointer'
                        }
                      `}
                      title={isLocked ? 'This notification is required and cannot be disabled' : (isDisabled ? 'Click "Make Changes" to edit' : '')}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                          ${isSendOn ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                    {isLocked && (
                      <svg
                        className="w-3 h-3 text-gray-400 absolute -right-4 top-1/2 -translate-y-1/2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* CC Provider toggle (patient/informant groups only) */}
                {item.hasCcProvider && (
                  <div className="col-span-3 flex justify-center">
                    <button
                      type="button"
                      disabled={isDisabled || !isSendOn}
                      onClick={() => handleToggle(activeGroup, item.key, 'ccProvider')}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1
                        ${!isSendOn || isDisabled
                          ? (isCcOn && isSendOn ? 'bg-primary cursor-not-allowed' : 'bg-gray-300 cursor-not-allowed')
                          : isCcOn
                            ? 'bg-primary cursor-pointer'
                            : 'bg-gray-300 cursor-pointer'
                        }
                      `}
                      title={!isSendOn ? 'Enable Send first to manage CC' : (isDisabled ? 'Click "Make Changes" to edit' : '')}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                          ${isCcOn ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end space-x-2 pt-2">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={isSaving}
                className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                  isSaving || !hasChanges
                    ? 'bg-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-primary hover:bg-primary/90'
                }`}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={enterEditMode}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-md hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Make Changes
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg z-50 max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title className="sr-only">
            Manage Notifications
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Manage which automated notifications your organisation sends.
          </Dialog.Description>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors z-10"
            aria-label="Close"
          >
            <CloseIcon />
          </button>

          {/* Animated height wrapper for smooth step transitions */}
          <div
            className="overflow-hidden transition-[height] duration-300 ease-in-out"
            style={{ height: animatedHeight }}
          >
            <div ref={contentRef} className="p-4 md:p-6">
              {activeGroup === 'history' ? (
                <ConfigHistoryTimeline
                  sections={HISTORY_SECTIONS}
                  onBack={() => { setActiveGroup(null); }}
                  title="Notification Preferences - Change History"
                  onContentChange={measureAndAnimate}
                />
              ) : activeGroup ? renderDetail() : renderMenu()}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
